import React from 'react';
import { useSelector } from 'react-redux';

// Types
import { ReviewState } from 'Shared/types/Review.type';

// Utils
import * as ReviewsUtils from 'Shared/utils/Reviews.utils';

// Helpers
import * as ReviewsHelpers from 'Shared/model_helpers/Reviews.helper';
import * as ReviewFormHelpers from 'Shared/model_helpers/ReviewForm.helper';
import * as UserHelpers from 'Shared/model_helpers/User.helpers';

// Selectors
import * as ReviewsSelectors from 'Shared/selectors/Reviews.selectors';
import * as UserSelectors from 'Shared/selectors/Users.selectors';

type Props = {
  review;
  isPeerReviewForm?: boolean;
};

const StatusDescription = ({ review, isPeerReviewForm = false }: Props) => {
  const currentUserSlug = useSelector(state => state.user.slug);

  const slug = ReviewsHelpers.getSlug(review);
  const state = ReviewsHelpers.getState(review);
  const self360 = ReviewsHelpers.getSelf360(review);
  const compilerSlug = ReviewsHelpers.getCompilerSlug(review);
  const receiverSlug = ReviewsHelpers.getReceiverSlug(review);
  const creatorSlug = ReviewsHelpers.getCreatorSlug(review);

  const receiver = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: receiverSlug }));
  const receiverName = UserHelpers.getDisplayName(receiver);
  const compiler = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: compilerSlug }));
  const compilerName = UserHelpers.getDisplayName(compiler);
  const creator = useSelector(state => UserSelectors.getUserByUserSlug(state, { userSlug: creatorSlug }));
  const CreatorName = UserHelpers.getDisplayName(creator);

  const compilerAndCreatorNames = [...new Set([compilerName, CreatorName])].join(', and ');
  const receiverAndCreatorNames = [...new Set([receiverName, CreatorName])].join(', and ');

  const reviewForm = useSelector(state =>
    ReviewsSelectors.getReviewFormsByReviewSlugAndUserSlug(state, { reviewSlug: slug, userSlug: currentUserSlug })
  );
  const hasReviewForm = Boolean(reviewForm);

  const isCurrentUserCompiler = ReviewsUtils.isCurrentUserCompiler({ review, currentUserSlug });
  const isCurrentUserReceiver = ReviewsUtils.isCurrentUserReceiver({ review, currentUserSlug });

  const allPeerReviewForms = useSelector(state =>
    ReviewsSelectors.getReviewFormsByReviewSlugAndType(state, { reviewSlug: slug, formType: 'peer_review' })
  );
  const completedReveiewForms = allPeerReviewForms.filter(reviewForm =>
    Boolean(ReviewFormHelpers.getSubmittedAt(reviewForm))
  );

  const getDescription = () => {
    if (isCurrentUserCompiler && self360) {
      return 'Self 360';
    }

    if (isPeerReviewForm) {
      const peerReviewUserSlug = ReviewFormHelpers.getUserSlug(reviewForm);
      if (peerReviewUserSlug === receiverSlug) {
        return `Self Review`;
      }
    }

    if (isCurrentUserReceiver) {
      if (state === ReviewState.SHARED) {
        if (currentUserSlug === creatorSlug) {
          return `Only you and ${compilerName} can view this`;
        } else {
          return `Only you and ${compilerAndCreatorNames} can view this`;
        }
      }
      return null;
    }

    if (isCurrentUserCompiler) {
      if (isPeerReviewForm) {
        return `Peer Feedback for ${receiverName}`;
      } else if (state === ReviewState.SHARED) {
        if (currentUserSlug === creatorSlug) {
          return `Only you and ${receiverName} can view this`;
        } else {
          return `Only you and ${receiverAndCreatorNames} can view this`;
        }
      } else if (state === ReviewState.COMPILATION_COMPLETED_BUT_NOT_SHARED) {
        return `Ready to share`;
      } else if (state === ReviewState.COMPILATION) {
        return `Ready to complete`;
      } else if (state === ReviewState.PEER_REVIEW && completedReveiewForms.size === 0) {
        return `Waiting for peer reviews`;
      } else if (state === ReviewState.PEER_REVIEW && completedReveiewForms.size > 0) {
        return `${completedReveiewForms.size} of ${allPeerReviewForms.size} reviews submitted`;
      }
      return null;
    }

    if (!isCurrentUserCompiler && !isCurrentUserReceiver && hasReviewForm) {
      const isReviewFormSubmitted = Boolean(ReviewFormHelpers.getStartedAt(reviewForm));

      if (state === ReviewState.PEER_REVIEW && !isReviewFormSubmitted) {
        return `Peer Feedback for ${receiverName}`;
      } else if (isReviewFormSubmitted) {
        if (currentUserSlug === creatorSlug) {
          return `Only you and ${compilerName} can view this`;
        } else {
          return `Only you and ${compilerAndCreatorNames} can view this`;
        }
      }
    }
    // Else we assume the current user is a peer
    return null;
  };

  return <span className="StatusDescription">{getDescription()}</span>;
};

export default StatusDescription;
